import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Skeleton } from 'antd';

import ButtonComponent from '@/components/Button';
import { ReactComponent as ValidIcon } from '@/assets/images/icons/valid.svg';
import { ReactComponent as SmsCreditSpentIcon } from '@/assets/images/icons/sms-credit-spent.svg';
import { ReactComponent as InvalidIcon } from '@/assets/images/icons/invalid.svg';
import { ReactComponent as SendIcon } from '@/assets/images/icons/send.svg';
import { ReactComponent as DownloadIcon } from '@/assets/images/icons/download.svg';

import './validate-data.scss';
import { useTranslation } from 'react-i18next';

function ValidateData({ data, loading, onBack, onSubmit }) {
  const [submiting, setSubmiting] = useState(false);

  const skeletonLoading = () => (
    <>
      <div className="result-data">
        <Skeleton.Avatar active size="large" shape="circle" />
        <Skeleton
          paragraph={{
            rows: 1,
          }}
        />
      </div>

      <div className="result-data">
        <Skeleton.Avatar active size="large" shape="circle" />
        <Skeleton
          paragraph={{
            rows: 1,
          }}
        />
      </div>

      <div className="result-data">
        <Skeleton.Avatar active size="large" shape="circle" />
        <Skeleton
          paragraph={{
            rows: 1,
          }}
        />
        <div className="download-error-file">
          <Skeleton.Button active size="large" />
        </div>
      </div>
    </>
  );

  const { validRowCount, totalSmsCredit, invalidRowCount, errorFile } = data;
  const { t } = useTranslation();
  const disabledSubmit =
    validRowCount === null &&
    totalSmsCredit === null &&
    invalidRowCount === null;

  const handleOnSend = async () => {
    setSubmiting(true);
    await onSubmit();
    setSubmiting(false);
  };

  const errorFileName = () => new URL(errorFile).pathname.split('/').pop();

  return (
    <div className="validate-data-container">
      <div className="content-wrap">
        {loading && skeletonLoading()}

        {!loading && (
          <>
            <div className="result-data total-rows-valid">
              <div className="icon">
                <ValidIcon />
              </div>
              <div className="information">
                <div className="title">
                  {t(
                    'sms_page.send_using_file.create.validate_data.total_valid_rows',
                  )}
                </div>
                <div className="number">
                  {validRowCount?.toLocaleString() || 0}
                </div>
              </div>
            </div>

            <div className="result-data total-sms-credit-spent">
              <div className="icon">
                <SmsCreditSpentIcon />
              </div>
              <div className="information">
                <div className="title">
                  {t(
                    'sms_page.send_using_file.create.validate_data.estimated_sms_credit',
                  )}
                </div>
                <div className="number">
                  {totalSmsCredit?.toLocaleString() || 0}
                </div>
              </div>
            </div>
            <div className="result-data total-invalid-rows">
              <div className="icon">
                <InvalidIcon />
              </div>
              <div className="information">
                <div className="title">
                  {t(
                    'sms_page.send_using_file.create.validate_data.total_invalid_rows',
                  )}
                </div>
                <div className="number">
                  {invalidRowCount?.toLocaleString() || 0}
                </div>
              </div>
              {!!errorFile && (
                <div className="download-error-file">
                  <a
                    href={errorFile}
                    download={errorFileName()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ButtonComponent type="default" icon={<DownloadIcon />}>
                      {t('sms_page.send_using_file.create.btn_download_file')}
                    </ButtonComponent>
                  </a>
                </div>
              )}
            </div>
          </>
        )}

        <div className="actions">
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <ButtonComponent
                type="default"
                onClick={onBack}
                disabled={loading || submiting}
              >
                {t('sms_page.send_using_file.create.btn_back')}
              </ButtonComponent>
            </Col>
            <Col span={12}>
              <ButtonComponent
                type="primary"
                icon={<SendIcon />}
                onClick={handleOnSend}
                disabled={loading || disabledSubmit}
                loading={submiting}
              >
                {t('sms_page.send_using_file.create.btn_send')}
              </ButtonComponent>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

ValidateData.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
};

export default ValidateData;
